import * as React from "react";
import { Input, Label } from "@eduviaos/ui-kit";

interface BrandColourPickerProps {
  id: string;
  label: string;
  description?: string;
  value: string | null;
  onChange: (next: string | null) => void;
  /** Default colour to seed the swatch when value is null. */
  defaultColor?: string;
}

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Brand colour picker — wraps `<input type="color">` with a
 * synchronised hex text input so SAs can paste a brand-spec hex
 * directly. Reports null when the SA clears the field (which the BE
 * interprets as "use platform default").
 */
export function BrandColourPicker({
  id,
  label,
  description,
  value,
  onChange,
  defaultColor = "#4f46e5",
}: BrandColourPickerProps) {
  const display = value ?? defaultColor;
  const [text, setText] = React.useState<string>(display);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setText(display);
  }, [display]);

  const commitText = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      onChange(null);
      setError(null);
      return;
    }
    if (!HEX_RE.test(trimmed)) {
      setError("Use #RGB or #RRGGBB hex (e.g. #4f46e5).");
      return;
    }
    setError(null);
    onChange(trimmed.toLowerCase());
  };

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          id={`${id}-swatch`}
          aria-label={`${label} colour swatch`}
          value={display}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value.toLowerCase());
          }}
          className="size-10 cursor-pointer appearance-none rounded-md border bg-transparent"
        />
        <Input
          id={id}
          value={text}
          placeholder={defaultColor}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commitText(e.target.value)}
          className="font-mono text-sm uppercase"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setText(defaultColor);
              setError(null);
            }}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Reset
          </button>
        )}
      </div>
      {description && <p className="text-muted-foreground text-xs">{description}</p>}
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
